import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Image,
  Input,
  Select,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import axios from "axios";
import { Field, Form, Formik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { FiPlus, FiUpload, FiX } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { REQUEST_MASTER_CASESTUDY } from "../../store/casestudy/casestudyActionTypes";
import { useCasestudyMaster } from "../../store/casestudy/reducer";
import { UploadVideoAndSelectThumbnail } from "../uploadVideo/UploadVideoAndSelectThumbnail";

interface CasestudyFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  bannerData?: any;
  casestudyData?: any;
  refreshData?: () => void;
}

const IMAGE_LIMITS = {
  overview: 2,
  designVision: 3,
  userFlow: 1,
  colorPalette: 1,
  wireFrame: 1,
  uiDesign: 1,
  metadata: 1,
  logo: 4,
  typography: 8,
  showcase: 1,
  projectDetails: 6,
  slider: 6,
};

interface BannerImageUploaderProps {
  label: string;
  fieldName: string;
  currentImage: string | undefined;
  setFieldValue: (field: string, value: any) => void;
  isVideo?: boolean;
}

const BannerMediaUploader: React.FC<BannerImageUploaderProps> = ({
  label,
  fieldName,
  currentImage,
  setFieldValue,
  isVideo = false,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const toast = useToast();

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);

    try {
      const file = files[0];
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/admin/image`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log("response", response.data[0]);
      const uploadedUrl = response.data[0].imageURL;

      setFieldValue(fieldName, uploadedUrl);

      toast({
        title: `${isVideo ? "Video" : "Image"} uploaded successfully`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error(`Error uploading ${isVideo ? "video" : "image"}:`, error);
      toast({
        title: `Failed to upload ${isVideo ? "video" : "image"}`,
        description: error instanceof Error ? error.message : "Unknown error",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
      e.target.value = "";
    }
  };

  const removeMedia = () => {
    setFieldValue(fieldName, "");
  };

  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <VStack spacing={4} align="stretch">
        {currentImage && (
          <Box position="relative" width="100%" maxWidth="300px">
            <Button
              size="sm"
              position="absolute"
              top={2}
              right={2}
              colorScheme="red"
              onClick={removeMedia}
              borderRadius="full"
              zIndex={1}
              p={0}
              minW="30px"
              height="30px"
            >
              <FiX />
            </Button>
            {isVideo ? (
              <video
                src={`${process.env.REACT_APP_IMAGE_BASE_URL}/${currentImage}`}
                controls
                style={{ maxHeight: "200px", width: "100%" }}
              />
            ) : (
              <Image
                src={`${process.env.REACT_APP_IMAGE_BASE_URL}/${currentImage}`}
                alt={label}
                borderRadius="md"
                maxH="200px"
                objectFit="contain"
                width="100%"
                border="1px solid"
                borderColor="gray.200"
              />
            )}
          </Box>
        )}

        {!currentImage && (
          <Button
            as="label"
            htmlFor={`media-upload-${fieldName}`}
            cursor="pointer"
            leftIcon={<FiUpload />}
            colorScheme="blue"
            variant="outline"
            isLoading={isUploading}
            height="100px"
            borderStyle="dashed"
          >
            <Text>Upload {label}</Text>
            <Input
              id={`media-upload-${fieldName}`}
              type="file"
              accept={isVideo ? "video/*" : "image/*"}
              onChange={handleUpload}
              display="none"
            />
          </Button>
        )}

        {currentImage && (
          <Button
            as="label"
            htmlFor={`media-upload-${fieldName}`}
            cursor="pointer"
            leftIcon={<FiUpload />}
            colorScheme="blue"
            variant="outline"
            isLoading={isUploading}
            size="sm"
          >
            <Text>Replace {label}</Text>
            <Input
              id={`media-upload-${fieldName}`}
              type="file"
              accept={isVideo ? "video/*" : "image/*"}
              onChange={handleUpload}
              display="none"
            />
          </Button>
        )}
      </VStack>
    </FormControl>
  );
};

const ServicesEditDrawer: React.FC<CasestudyFormDrawerProps> = ({
  isOpen,
  onClose,
  bannerData,
  casestudyData,
  refreshData,
}) => {
  console.log("casestudyData", casestudyData);
  console.log("bannerData", bannerData);
  const { masterCasestudy, busy } = useCasestudyMaster();
  const dispatch = useDispatch();
  const toast = useToast();
  const [bannerCategoryNames, setBannerCategoryNames] = useState<any[]>([]);
  const [bannerCategoryFilter, setBannerCategoryFilter] = useState<string>(
    bannerData?.type || ""
  );
  const [bannerSubCategoryNames, setBannerSubCategoryNames] = useState<any[]>(
    []
  );
  const [bannerSubCategoryFilter, setBannerSubCategoryFilter] =
    useState<string>(bannerData?.subCategory || "");
  const [mainCategories, setMainCategories] = useState<any>([]);
  const [subCategories, setSubCategories] = useState<any>([]);
  const [serviceTypes, setServiceTypes] = useState<any>([]);
  const [selectedMainCategory, setSelectedMainCategory] = useState<string>("");
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>("");
  const [selectedServiceType, setSelectedServiceType] = useState<string>("");
  const [newBannerCategory, setNewBannerCategory] = useState<string>("");
  const [newBannerSubCategory, setNewBannerSubCategory] = useState<string>("");
  const [selectedServices, setSelectedServices] = useState<any[]>([]);

  useEffect(() => {
    if (bannerData?.serviceId && Array.isArray(bannerData.serviceId)) {
      const initialServices = bannerData.serviceId.map((service: any) => ({
        id: service._id,
        title: service.title,
        mainCategory: service.mainCategoryId?.title || "",
        subCategory: service.subCategoryId?.title || "",
      }));
      setSelectedServices(initialServices);
    }
  }, [bannerData]);

  const initialValues = useMemo(() => {
    if (bannerData || casestudyData) {
      return {
        banner_imageURL: bannerData?.imageURL || "",
        banner_videoURL: bannerData?.videoURL || "",
        banner_thumbUrl: bannerData?.thumbUrl || "",
        banner_title: bannerData?.title || "",
        banner_description: bannerData?.description || "",
        banner_type: bannerData?.type || "",
        banner_subCategory: bannerData?.subCategory || "",
        banner_titleUrl: bannerData?.titleUrl || "",
        banner_metaDescription: bannerData?.metaDescription || "",
        banner_metaTitle: bannerData?.metaTitle || "",
        main_category: bannerData?.mainCategoryId || "",
        sub_category: bannerData?.subCategoryId || "",
        service_type: bannerData?.serviceId || [],
        title: casestudyData?.title || "",
        subtitle: casestudyData?.subtitle || "",
        description: casestudyData?.description || "",
      };
    }
    return {
      banner_imageURL: "",
      banner_videoURL: "",
      banner_thumbUrl: "",
      banner_title: "",
      banner_metaDescription: "",
      banner_metaTitle: "",
      banner_description: "",
      banner_type: "",
      banner_subCategory: "",
      banner_titleUrl: "",
      main_category: "",
      sub_category: "",
      service_type: [],
      title: "",
      subtitle: "",
      description: "",
    };
  }, [bannerData, casestudyData]);

  useEffect(() => {
    const getBannerCategoryNames = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/category/names`
        );
        setBannerCategoryNames(response.data);
        if (bannerData?.type && !response.data.includes(bannerData.type)) {
          setBannerCategoryNames((prev) => [...prev, bannerData.type]);
        }
      } catch (error) {
        console.log(error);
      }
    };

    getBannerCategoryNames();
  }, []);

  useEffect(() => {
    const getBannerSubCategoriesNames = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/category/subcategory/names`
        );
        setBannerSubCategoryNames(response.data);
        if (
          bannerData?.subCategory &&
          !response.data.includes(bannerData.subCategory)
        ) {
          setBannerSubCategoryNames((prev) => [
            ...prev,
            bannerData.subCategory,
          ]);
        }
      } catch (error) {
        console.log(error);
      }
    };

    getBannerSubCategoriesNames();
  }, []);

  useEffect(() => {
    const getMainCategories = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/admin/mainCategory`
        );
        setMainCategories(response.data);
        console.log("mainCategories", response.data);
      } catch (error) {
        console.log(error);
      }
    };

    getMainCategories();
  }, []);

  useEffect(() => {
    const getSubCategories = async () => {
      if (!selectedMainCategory) {
        setSubCategories([]);
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/admin/subCategory?mainCategoryId=${selectedMainCategory}`
        );
        setSubCategories(response.data);
        setSelectedSubCategory("");
        setSelectedServiceType("");
        setServiceTypes([]);
      } catch (error) {
        console.log(error);
        toast({
          title: "Error",
          description: "Failed to fetch subcategories",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    };

    getSubCategories();
  }, [selectedMainCategory]);

  useEffect(() => {
    const getServiceTypes = async () => {
      if (!selectedSubCategory) {
        setServiceTypes([]);
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/admin/service?subCategoryId=${selectedSubCategory}`
        );
        setServiceTypes(response.data);

        setSelectedServiceType("");
      } catch (error) {
        console.log(error);
        toast({
          title: "Error",
          description: "Failed to fetch service types",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    };

    getServiceTypes();
  }, [selectedSubCategory]);

  const handleAddService = (setFieldValue: any) => {
    if (!selectedServiceType || !selectedMainCategory || !selectedSubCategory)
      return;

    const selectedService = serviceTypes.find(
      (service: any) => service._id === selectedServiceType
    );
    if (!selectedService) return;

    const mainCategoryInfo = mainCategories.find(
      (cat: any) => cat._id === selectedMainCategory
    );
    const subCategoryInfo = subCategories.find(
      (cat: any) => cat._id === selectedSubCategory
    );

    const newService = {
      id: selectedService._id,
      title: selectedService.title,
      mainCategory: mainCategoryInfo?.title || "",
      subCategory: subCategoryInfo?.title || "",
    };

    // Check if this service is already selected
    const isAlreadySelected = selectedServices.some(
      (service) => service.id === newService.id
    );
    if (isAlreadySelected) {
      toast({
        title: "Service already selected",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    const updatedServices = [...selectedServices, newService];
    setSelectedServices(updatedServices);

    // Update the form field with service IDs
    setFieldValue(
      "service_type",
      updatedServices.map((s) => s.id)
    );

    // Reset the service type dropdown for next selection
    setSelectedServiceType("");
  };

  const handleRemoveService = (serviceId: string, setFieldValue: any) => {
    const updatedServices = selectedServices.filter(
      (service) => service.id !== serviceId
    );
    setSelectedServices(updatedServices);

    // Update the form field with remaining service IDs
    setFieldValue(
      "service_type",
      updatedServices.map((s) => s.id)
    );
  };

  const handleAddBannerCategory = () => {
    if (
      newBannerCategory.trim() &&
      !bannerCategoryNames.includes(newBannerCategory)
    ) {
      setBannerCategoryNames((prev) => [...prev, newBannerCategory]);
      setBannerCategoryFilter(newBannerCategory);
      setNewBannerCategory("");
    }
  };

  const handleAddBannerSubCategory = () => {
    if (
      newBannerSubCategory.trim() &&
      !bannerSubCategoryNames.includes(newBannerSubCategory)
    ) {
      setBannerSubCategoryNames((prev) => [...prev, newBannerSubCategory]);
      setBannerSubCategoryFilter(newBannerSubCategory);
      setNewBannerSubCategory("");
    }
  };

  const handleSubmit = async (
    values: any,
    { setSubmitting, setErrors, setFieldError }: any
  ) => {
    console.log("Form values:", values);
    const validationErrors: any = {};

    if (!values.banner_title || values.banner_title.trim() === "") {
      validationErrors.banner_title = "Banner Title is required";
    }
    if (!values.banner_description || values.banner_description.trim() === "") {
      validationErrors.banner_description = "Banner description is required";
    }

    if (!values.banner_imageURL || values.banner_imageURL.trim() === "") {
      validationErrors.banner_imageURL = "Banner Image is required";
    }

    if (!values.banner_type || values.banner_type.trim() === "") {
      validationErrors.banner_type = "Banner Category Type is required";
    }

    if (!values.banner_subCategory || values.banner_subCategory.trim() === "") {
      validationErrors.banner_subCategory = "Banner Sub Category is required";
    }



    // if (!selectedMainCategory) {
    //   validationErrors.main_category = "Main Category is required";
    // }

    // if (!selectedSubCategory) {
    //   validationErrors.sub_category = "Sub Category is required";
    // }
    if (selectedServices.length === 0) {
      validationErrors.service_type = "At least one Service Type is required";
    }
    // if (!selectedServiceType) {
    //   validationErrors.service_type = "Service Type is required";
    // }
    if (!values.banner_titleUrl || values.banner_titleUrl.trim() === "") {
      validationErrors.banner_titleUrl = "Banner Title URL is required";
    } else if (values.banner_titleUrl.includes("/")) {
      validationErrors.banner_titleUrl =
        "Banner Title URL must not contain '/'";
    }
    if (Object.keys(validationErrors).length > 0) {
      Object.keys(validationErrors).forEach((key) => {
        setFieldError(key, validationErrors[key]);
      });
      setSubmitting(false);
      return;
    }

    try {
      const bannerPayload: any = {
        banner_title: values.banner_title,
        banner_description: values.banner_description,
        banner_type: bannerCategoryFilter,
        banner_imageURL: values.banner_imageURL,
        banner_metaDescription: values.banner_metaDescription,
        banner_metaTitle: values.banner_metaTitle,
        banner_subCategory: values.banner_subCategory,
        serviceId: selectedServices.map((service) => service.id),
        banner_titleUrl: values.banner_titleUrl,
        // serviceId: [selectedServiceType],
      };

      if (values.banner_videoURL && values.banner_videoURL.trim() !== "") {
        bannerPayload.banner_videoURL = values.banner_videoURL;
      }
      if (values.banner_thumbUrl && values.banner_thumbUrl.trim() !== "") {
        bannerPayload.banner_thumbUrl = values.banner_thumbUrl;
      }
      if (bannerData?._id) {
        try {
          const bannerDetailData = {
            caseStydyId: bannerData.caseStydyId,
            banner_title: bannerPayload.banner_title,
            banner_metaDescription: bannerPayload.banner_metaDescription,
            banner_metaTitle: bannerPayload.banner_metaTitle,
            banner_description: bannerPayload.banner_description,
            banner_type: bannerPayload.banner_type,
            banner_imageURL: bannerPayload.banner_imageURL,
            banner_subCategory: bannerPayload.banner_subCategory,
            banner_titleUrl: bannerPayload.banner_titleUrl,
            serviceId: bannerPayload.serviceId,
            ...(bannerPayload.banner_videoURL && {
              banner_videoURL: bannerPayload.banner_videoURL,
            }),
            ...(bannerPayload.banner_thumbUrl && {
              banner_thumbUrl: bannerPayload.banner_thumbUrl,
            }),
          };

          console.log("bannerDetailData for update:", bannerDetailData);

          const bannerUpdateResponse = await axios({
            method: "patch",
            url: `${process.env.REACT_APP_BASE_URL}/admin/category/update/${bannerData._id}`,
            data: bannerDetailData,
          });

          console.log("Banner update response:", bannerUpdateResponse);
          dispatch({ type: REQUEST_MASTER_CASESTUDY });
          if (refreshData) {
            refreshData();
          }

          toast({
            title: "Success",
            description: "Case study updated successfully",
            status: "success",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          });

          onClose();
        } catch (error) {
          console.error("Error updating banner:", error);
          toast({
            title: "Error updating banner",
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          });
        }
      } else {
        try {
          const bannerDetailData = {
            banner_title: bannerPayload.banner_title,
            banner_description: bannerPayload.banner_description,
            banner_metaDescription: bannerPayload.banner_metaDescription,
            banner_metaTitle: bannerPayload.banner_metaTitle,
            banner_type: bannerPayload.banner_type,
            banner_subCategory: bannerPayload.banner_subCategory,
            banner_imageURL: bannerPayload.banner_imageURL,
            serviceId: bannerPayload.serviceId,
            banner_titleUrl: bannerPayload.banner_titleUrl,
            ...(bannerPayload.banner_videoURL && {
              banner_videoURL: bannerPayload.banner_videoURL,
            }),
          };

          console.log("bannerDetailData for create:", bannerDetailData);

          const bannerCreateResponse = await axios({
            method: "post",
            url: `${process.env.REACT_APP_BASE_URL}/admin/category/create`,
            data: bannerDetailData,
          });

          console.log("Banner create response:", bannerCreateResponse);
          dispatch({ type: REQUEST_MASTER_CASESTUDY });

          if (refreshData) {
            refreshData();
          }
          toast({
            title: "Success",
            description: "Case study created successfully",
            status: "success",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          });

          onClose();
        } catch (error) {
          console.error("Error creating banner:", error);
          toast({
            title: "Error creating banner",
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          });
        }
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
      <DrawerOverlay />
      <DrawerContent>
        <Formik initialValues={initialValues} onSubmit={handleSubmit}>
          {({ values, errors, touched, setFieldValue, isSubmitting }) => (
            <Form
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <DrawerHeader
                borderBottom="1px solid #E0E0E0"
                background="#f2f2f2"
                alignItems={"stretch"}
                flex="0 0 auto"
              >
                {bannerData ? "Edit" : "Add"} Case Study
              </DrawerHeader>

              <DrawerBody
                flex="1 1 auto"
                overflowY="auto"
                css={{
                  "&::-webkit-scrollbar": {
                    width: "4px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#f1f1f1",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#888",
                    borderRadius: "2px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: "#555",
                  },
                }}
              >
                <VStack spacing={6} width="100%" align="stretch">
                  <VStack
                    spacing={4}
                    align="stretch"
                    width="100%"
                    p={4}
                    borderWidth="1px"
                    borderRadius="md"
                  >
                    <Heading size="md" fontWeight="bold">
                      Banner Information
                    </Heading>
                    <VStack spacing={4} align="stretch">
                      <FormControl>
                        <FormLabel>Banner Meta Title</FormLabel>
                        <Field
                          as={Input}
                          name="banner_metaTitle"
                          placeholder="Banner Meta Title"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Banner Meta Description</FormLabel>
                        <Field
                          as={Input}
                          name="banner_metaDescription"
                          placeholder="Banner Meta Description"
                        />
                      </FormControl>
                      <FormControl
                        isInvalid={
                          !!errors.banner_title && !!touched.banner_title
                        }
                      >
                        <FormLabel>Banner Title</FormLabel>
                        <Field
                          as={Input}
                          name="banner_title"
                          placeholder="Banner Title"
                        />
                        <FormErrorMessage>
                          {typeof errors.banner_title === "string"
                            ? errors.banner_title
                            : undefined}
                        </FormErrorMessage>
                      </FormControl>
                      <FormControl
                        isInvalid={
                          !!errors.banner_description &&
                          !!touched.banner_description
                        }
                      >
                        <FormLabel>Banner Description</FormLabel>
                        <Field
                          as={Input}
                          name="banner_description"
                          placeholder="Banner Description"
                        />
                        <FormErrorMessage>
                          {typeof errors.banner_description === "string"
                            ? errors.banner_description
                            : undefined}
                        </FormErrorMessage>
                      </FormControl>

                      <FormControl
                        isInvalid={
                          !!errors.banner_type && !!touched.banner_type
                        }
                      >
                        <FormLabel>Banner Category Type</FormLabel>
                        <HStack>
                          <Box flex="1" marginRight={2}>
                            <Select
                              value={values.banner_type || bannerCategoryFilter}
                              onChange={(event) => {
                                setBannerCategoryFilter(event.target.value);
                                setFieldValue(
                                  "banner_type",
                                  event.target.value
                                );
                              }}
                              bg="#FFF"
                            >
                              {bannerCategoryNames.map((category) => (
                                <option key={category} value={category}>
                                  {category}
                                </option>
                              ))}
                            </Select>
                            <FormErrorMessage>
                              {typeof errors.banner_type === "string"
                                ? errors.banner_type
                                : undefined}
                            </FormErrorMessage>
                          </Box>
                          <HStack>
                            <Input
                              placeholder="New Category"
                              value={newBannerCategory}
                              onChange={(e) =>
                                setNewBannerCategory(e.target.value)
                              }
                              width="150px"
                            />
                            <Button
                              colorScheme="green"
                              size="sm"
                              onClick={handleAddBannerCategory}
                              isDisabled={!newBannerCategory.trim()}
                            >
                              <FiPlus />
                            </Button>
                          </HStack>
                        </HStack>
                      </FormControl>
                      <FormControl
                        isInvalid={
                          !!errors.banner_subCategory &&
                          !!touched.banner_subCategory
                        }
                      >
                        <FormLabel>Banner Sub Category Type</FormLabel>
                        <HStack>
                          <Box flex="1" marginRight={2}>
                            <Select
                              value={
                                values.banner_subCategory ||
                                bannerSubCategoryFilter
                              }
                              onChange={(event) => {
                                setBannerSubCategoryFilter(event.target.value);
                                setFieldValue(
                                  "banner_subCategory",
                                  event.target.value
                                );
                              }}
                              bg="#FFF"
                            >
                              {bannerSubCategoryNames.map((category) => (
                                <option key={category} value={category}>
                                  {category}
                                </option>
                              ))}
                            </Select>
                            <FormErrorMessage>
                              {typeof errors.banner_subCategory === "string"
                                ? errors.banner_subCategory
                                : undefined}
                            </FormErrorMessage>
                          </Box>
                          <HStack>
                            <Input
                              placeholder="New Sub Category"
                              value={newBannerSubCategory}
                              onChange={(e) =>
                                setNewBannerSubCategory(e.target.value)
                              }
                              width="150px"
                            />
                            <Button
                              colorScheme="green"
                              size="sm"
                              onClick={handleAddBannerSubCategory}
                              isDisabled={!newBannerSubCategory.trim()}
                            >
                              <FiPlus />
                            </Button>
                          </HStack>
                        </HStack>
                      </FormControl>
                      <FormControl
                        isInvalid={
                          !!errors.banner_titleUrl && !!touched.banner_titleUrl
                        }
                      >
                        <FormLabel>Banner Title URL</FormLabel>
                        <Field
                          as={Input}
                          name="banner_titleUrl"
                          placeholder="Banner Title URL"
                        />
                        <FormErrorMessage>
                          {typeof errors.banner_titleUrl === "string"
                            ? errors.banner_titleUrl
                            : undefined}
                        </FormErrorMessage>
                      </FormControl>

                      <FormControl
                        isInvalid={
                          !!errors.banner_imageURL && !!touched.banner_imageURL
                        }
                      >
                        <FormLabel>
                          Banner Image{" "}
                          <Text as="span" color="red.500">
                            *
                          </Text>
                        </FormLabel>
                        <BannerMediaUploader
                          label="Banner Image"
                          fieldName="banner_imageURL"
                          currentImage={values.banner_imageURL}
                          setFieldValue={setFieldValue}
                          isVideo={false}
                        />
                        <FormErrorMessage>
                          {typeof errors.banner_imageURL === "string"
                            ? errors.banner_imageURL
                            : undefined}
                        </FormErrorMessage>
                      </FormControl>
                      <UploadVideoAndSelectThumbnail
                        label="Banner Video"
                        fieldName="banner_videoURL"
                        currentImage={values.banner_videoURL}
                        setFieldValue={setFieldValue}
                        isVideo={true}
                        enforceImageDimensions={false}
                        isWatermark={false}
                        videoError={errors.banner_videoURL || ""}
                        imageFieldName="banner_thumbUrl"
                        currentImageForThumbnail={values.banner_thumbUrl}
                      />
                      {/* <BannerMediaUploader
                        label="Banner Video"
                        fieldName="banner_videoURL"
                        currentImage={values.banner_videoURL}
                        setFieldValue={setFieldValue}
                        isVideo={true}
                      /> */}
                      <VStack>
                        <HStack w={"100%"}>
                          <FormControl
                            isInvalid={
                              !!errors.main_category && !!touched.main_category
                            }
                          >
                            <FormLabel>Main Category</FormLabel>
                            <Box marginRight={5}>
                              <Select
                                placeholder="Select Main Category"
                                value={selectedMainCategory}
                                onChange={(e) => {
                                  const mainCategoryId = e.target.value;
                                  setSelectedMainCategory(mainCategoryId);
                                  setFieldValue(
                                    "main_category",
                                    mainCategoryId
                                  );
                                }}
                                bg="#FFF"
                              >
                                {mainCategories.map((category: any) => (
                                  <option
                                    key={category._id}
                                    value={category._id}
                                  >
                                    {category.title}
                                  </option>
                                ))}
                              </Select>
                              <FormErrorMessage>
                                {typeof errors.main_category === "string"
                                  ? errors.main_category
                                  : undefined}
                              </FormErrorMessage>
                            </Box>
                          </FormControl>
                          <FormControl
                            isInvalid={
                              !!errors.sub_category && !!touched.sub_category
                            }
                          >
                            <FormLabel>Sub Category</FormLabel>
                            <Box marginRight={5}>
                              <Select
                                placeholder="Select Sub Category"
                                value={selectedSubCategory}
                                onChange={(e) => {
                                  const subCategoryId = e.target.value;
                                  setSelectedSubCategory(subCategoryId);
                                  setFieldValue("sub_category", subCategoryId);
                                }}
                                bg="#FFF"
                                isDisabled={!selectedMainCategory}
                              >
                                {subCategories.map((subCategory: any) => (
                                  <option
                                    key={subCategory._id}
                                    value={subCategory._id}
                                  >
                                    {subCategory.title}
                                  </option>
                                ))}
                              </Select>
                              <FormErrorMessage>
                                {typeof errors.sub_category === "string"
                                  ? errors.sub_category
                                  : undefined}
                              </FormErrorMessage>
                            </Box>
                          </FormControl>
                        </HStack>
                        <FormControl
                          isInvalid={
                            !!errors.service_type && !!touched.service_type
                          }
                        >
                          <FormLabel>Type of Services</FormLabel>
                          <HStack>
                            <Box marginRight={5}>
                              <Select
                                placeholder="Select Service Type"
                                value={selectedServiceType}
                                onChange={(e) => {
                                  const serviceId = e.target.value;
                                  setSelectedServiceType(serviceId);
                                  setFieldValue("service_type", serviceId);
                                }}
                                bg="#FFF"
                                isDisabled={!selectedSubCategory}
                              >
                                {serviceTypes.map((service: any) => (
                                  <option key={service._id} value={service._id}>
                                    {service.title}
                                  </option>
                                ))}
                              </Select>
                              <FormErrorMessage>
                                {typeof errors.service_type === "string"
                                  ? errors.service_type
                                  : undefined}
                              </FormErrorMessage>
                            </Box>
                            <Button
                              colorScheme="blue"
                              onClick={() => handleAddService(setFieldValue)}
                              isDisabled={!selectedServiceType}
                            >
                              <FiPlus />
                            </Button>
                          </HStack>
                        </FormControl>
                        <Box mt={4} width="100%">
                          {/* <FormLabel>Selected Services</FormLabel> */}
                          <Box>
                            {selectedServices.length === 0 ? (
                              <Text color="gray.500" fontSize="sm">
                                No services selected
                              </Text>
                            ) : (
                              selectedServices.map((service) => (
                                <Box
                                  key={service.id}
                                  display="inline-flex"
                                  alignItems="center"
                                  bg="blue.50"
                                  color="blue.700"
                                  borderRadius="md"
                                  px={2}
                                  py={1}
                                  mr={2}
                                  mb={2}
                                  fontSize="sm"
                                >
                                  <Text>
                                    {service.title} ({service.mainCategory}:
                                    {service.subCategory})
                                  </Text>
                                  <Button
                                    size="xs"
                                    variant="ghost"
                                    ml={1}
                                    onClick={() =>
                                      handleRemoveService(
                                        service.id,
                                        setFieldValue
                                      )
                                    }
                                    p={0}
                                    minW="auto"
                                    height="auto"
                                    color="blue.700"
                                  >
                                    <FiX size={14} />
                                  </Button>
                                </Box>
                              ))
                            )}
                          </Box>
                        </Box>
                      </VStack>
                    </VStack>
                  </VStack>
                </VStack>
              </DrawerBody>

              <DrawerFooter
                borderTopWidth="1px"
                flex="0 0 auto"
                bg="white"
                position="sticky"
                bottom={0}
                zIndex={1}
              >
                <Button
                  variant="outline"
                  mr={3}
                  border="1px solid #EEE"
                  borderRadius="6px"
                  color="#424242"
                  fontSize={"sm"}
                  onClick={onClose}
                  type="button"
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  type="submit"
                  isLoading={false}
                  background="#000"
                  fontSize={"sm"}
                  color="#fff"
                  _hover={{ background: "#000" }}
                >
                  {bannerData ? "Update" : "Create"} Case Study
                </Button>
              </DrawerFooter>
            </Form>
          )}
        </Formik>
      </DrawerContent>
    </Drawer>
  );
};

export default ServicesEditDrawer;
